import React, { useState, useEffect } from "react";
import { Typography, Grid, Card, Avatar, Button, Box, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton } from "@mui/material";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment/min/moment-with-locales";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { GoogleMap, Marker, DirectionsRenderer } from "@react-google-maps/api";
import { makeStyles } from "@mui/styles";
import { MAIN_COLOR } from "../common/sharedFunctions";
import { api } from "common";
import ConfirmationDialogRaw from "../components/ConfirmationDialogRaw";

const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: "10px",
    backgroundColor: "var(--bg-card)",
    color: "var(--text-main)",
    border: "var(--card-border)",
    minHeight: 100,
    marginTop: 5,
    marginBottom: 20,
    boxShadow: "var(--shadow)",
  },
  txt: {
    padding: 10,
    fontWeight: "bold",
    minHeight: 60,
    backgroundColor: "var(--bg-main)",
    color: "var(--primary)",
    borderBottom: "1px solid var(--border-color)",
    boxShadow: "none",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
}));

function BookingDetails() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const bookinglistdata = useSelector((state) => state.bookinglistdata);
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const settings = useSelector((state) => state.settingsdata.settings);
  const classes = useStyles();
  const auth = useSelector(state => state.auth);
  const role = useSelector(state => state.auth.profile.usertype);
  const dispatch = useDispatch();
  const { cancelBooking, updateBooking } = api;
  const [openConfirm, setOpenConfirm] = useState(false);
  const [openOtpDialog, setOpenOtpDialog] = useState(false);
  const [otpInput, setOtpInput] = useState("");
  const [otpError, setOtpError] = useState("");
  const [openRatingDialog, setOpenRatingDialog] = useState(false);
  const [ratingValue, setRatingValue] = useState(0);
  const [feedbackText, setFeedbackText] = useState("");
  const cartypes = useSelector((state) => state.cartypes.cars);
  const [loadingTimer, setLoadingTimer] = useState({
    elapsedMins: 0,
    elapsedSecs: 0,
    remainingMins: 10,
    remainingSecs: 0,
    isExceeded: false,
    extraMins: 0,
    extraCharge: 0
  });

  useEffect(() => {
    let intervalId = null;
    if (data && data.status === 'ARRIVED' && data.driver_arrive_time) {
      const arriveTime = parseInt(data.driver_arrive_time);
      const freeTimeLimitMins = settings && settings.freeWaitingTime !== undefined ? parseInt(settings.freeWaitingTime) : 10;
      const freeTimeLimitSecs = freeTimeLimitMins * 60;

      const updateTimer = () => {
        const now = new Date().getTime();
        const diffMs = now - arriveTime;
        if (diffMs > 0) {
          const totalSecs = Math.floor(diffMs / 1000);
          const elapsedMins = Math.floor(totalSecs / 60);
          const elapsedSecs = totalSecs % 60;
          
          let isExceeded = false;
          let remainingMins = 0;
          let remainingSecs = 0;
          let extraMins = 0;
          let extraCharge = 0;

          if (totalSecs > freeTimeLimitSecs) {
            isExceeded = true;
            const exceededSecs = totalSecs - freeTimeLimitSecs;
            extraMins = Math.floor(exceededSecs / 60);
            
            let rate_per_minute = 0;
            if (cartypes && data.carType) {
              const selectedCarType = cartypes.find(t => t.name === data.carType);
              if (selectedCarType) {
                rate_per_minute = selectedCarType.extra_loading_rate !== undefined 
                  ? parseFloat(selectedCarType.extra_loading_rate) 
                  : (selectedCarType.rate_per_hour ? parseFloat(selectedCarType.rate_per_hour) / 60 : 0);
              }
            }
            extraCharge = extraMins * rate_per_minute;
          } else {
            const remainingTotalSecs = freeTimeLimitSecs - totalSecs;
            remainingMins = Math.floor(remainingTotalSecs / 60);
            remainingSecs = remainingTotalSecs % 60;
          }

          setLoadingTimer({
            elapsedMins,
            elapsedSecs,
            remainingMins,
            remainingSecs,
            isExceeded,
            extraMins,
            extraCharge
          });
        }
      };

      updateTimer();
      intervalId = setInterval(updateTimer, 1000);
    } else {
      const freeTimeLimitMins = settings && settings.freeWaitingTime !== undefined ? parseInt(settings.freeWaitingTime) : 10;
      setLoadingTimer({
        elapsedMins: 0,
        elapsedSecs: 0,
        remainingMins: freeTimeLimitMins,
        remainingSecs: 0,
        isExceeded: false,
        extraMins: 0,
        extraCharge: 0
      });
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [data, settings, cartypes]);

  const locationdata = useSelector((state) => state.locationdata);
  const driverCoords = locationdata && locationdata.coords ? locationdata.coords : null;
  const [directionsResponse, setDirectionsResponse] = useState(null);

  useEffect(() => {
    if (data && window.google) {
      let originCoords = null;
      let destCoords = null;

      if (['ACCEPTED', 'ARRIVED'].includes(data.status)) {
        if (driverCoords) {
          originCoords = { lat: parseFloat(driverCoords.lat), lng: parseFloat(driverCoords.lng) };
        } else if (data.pickup) {
          originCoords = { lat: parseFloat(data.pickup.lat), lng: parseFloat(data.pickup.lng) };
        }
        if (data.pickup) {
          destCoords = { lat: parseFloat(data.pickup.lat), lng: parseFloat(data.pickup.lng) };
        }
      } else if (data.status === 'STARTED') {
        if (driverCoords) {
          originCoords = { lat: parseFloat(driverCoords.lat), lng: parseFloat(driverCoords.lng) };
        } else if (data.pickup) {
          originCoords = { lat: parseFloat(data.pickup.lat), lng: parseFloat(data.pickup.lng) };
        }
        if (data.drop) {
          destCoords = { lat: parseFloat(data.drop.lat), lng: parseFloat(data.drop.lng) };
        }
      } else {
        if (data.pickup) {
          originCoords = { lat: parseFloat(data.pickup.lat), lng: parseFloat(data.pickup.lng) };
        }
        if (data.drop) {
          destCoords = { lat: parseFloat(data.drop.lat), lng: parseFloat(data.drop.lng) };
        }
      }

      if (originCoords && destCoords && (originCoords.lat !== destCoords.lat || originCoords.lng !== destCoords.lng)) {
        const directionsService = new window.google.maps.DirectionsService();
        directionsService.route(
          {
            origin: new window.google.maps.LatLng(originCoords.lat, originCoords.lng),
            destination: new window.google.maps.LatLng(destCoords.lat, destCoords.lng),
            travelMode: window.google.maps.TravelMode.DRIVING
          },
          (result, status) => {
            if (status === window.google.maps.DirectionsStatus.OK) {
              setDirectionsResponse(result);
            } else {
              console.error("Directions request failed: ", status);
              setDirectionsResponse(null);
            }
          }
        );
      } else {
        setDirectionsResponse(null);
      }
    } else {
      setDirectionsResponse(null);
    }
  }, [data?.status, data?.pickup?.lat, data?.pickup?.lng, data?.drop?.lat, data?.drop?.lng, driverCoords?.lat, driverCoords?.lng]);

  const handleArrive = () => {
    dispatch(updateBooking({ ...data, status: 'ARRIVED' }));
  };

  const handleStartTrip = () => {
    if (data.otp) {
      setOtpError("");
      setOtpInput("");
      setOpenOtpDialog(true);
    } else {
      dispatch(updateBooking({ ...data, status: 'STARTED' }));
    }
  };

  const handleOtpVerify = () => {
    if (otpInput === data.otp.toString()) {
      setOpenOtpDialog(false);
      dispatch(updateBooking({ ...data, status: 'STARTED' }));
    } else {
      setOtpError(t("otp_not_match") || "OTP does not match. Please try again.");
    }
  };

  const handleEndTrip = () => {
    dispatch(updateBooking({ ...data, status: 'REACHED' }));
  };

  const handleCompletePayment = () => {
    const tripCost = parseFloat(data.payableAmount || data.trip_cost || 0).toFixed(settings.decimal || 2);
    const updated = {
      ...data,
      status: 'PAID',
      customer_paid: tripCost
    };
    if (data.payment_mode === 'cash') {
      updated.cashPaymentAmount = tripCost;
      updated.cardPaymentAmount = 0;
      updated.usedWalletMoney = 0;
    } else if (data.payment_mode === 'wallet') {
      updated.cashPaymentAmount = 0;
      updated.cardPaymentAmount = 0;
      updated.usedWalletMoney = tripCost;
    } else {
      updated.cashPaymentAmount = 0;
      updated.cardPaymentAmount = tripCost;
      updated.usedWalletMoney = 0;
    }
    dispatch(updateBooking(updated));
  };

  const handleCustomerPay = () => {
    if (data.payment_mode === 'card') {
      navigate('/bookings');
      alert(t('pay_now_from_list') || 'Please complete card payment from the Bookings History list.');
    } else if (data.payment_mode === 'wallet') {
      const tripCost = parseFloat(data.payableAmount || data.trip_cost || 0);
      if (parseFloat(auth.profile.walletBalance || 0) < tripCost) {
        alert(t('wallet_balance_low') || 'Insufficient wallet balance.');
        return;
      }
      handleCompletePayment();
    } else {
      handleCompletePayment();
    }
  };

  const handleSkipRating = () => {
    setOpenRatingDialog(false);
    const updated = {
      ...data,
      rating: 0,
      feedback: "-"
    };
    dispatch(updateBooking(updated));
  };

  const handleSubmitRating = () => {
    if (ratingValue === 0) return;
    setOpenRatingDialog(false);
    const updated = {
      ...data,
      rating: ratingValue,
      feedback: feedbackText.trim() !== "" ? feedbackText : "-"
    };
    dispatch(updateBooking(updated));
  };

  const handleCancelBooking = () => {
    if (
      settings.AllowCriticalEditsAdmin &&
      (role === "customer" ||
        role === "admin" ||
        role === "fleetadmin")
    ) {
      setOpenConfirm(true);
    } else {
      alert(t("demo_mode"));
    }
  };

  const onConfirmClose = (value) => {
    if (value) {
      dispatch(
        cancelBooking({
          reason: value,
          booking: data,
          cancelledBy: role,
        })
      );
    }
    setOpenConfirm(false);
  };

  useEffect(() => {
    if (bookinglistdata.bookings) {
      const booking = bookinglistdata.bookings.filter(
        (item) => item.id === id.toString()
      )[0];

      if (booking) {
        setData(booking);
      } else {
        navigate("/404");
        setData([]);
      }
    }
  }, [bookinglistdata.bookings, id, navigate]);

  useEffect(() => {
    if (data && data.status === 'COMPLETE' && role === 'customer' && data.rating === undefined) {
      setOpenRatingDialog(true);
    }
  }, [data, role]);

  const renderGridItem = (item, isRTL) => {
    return (
      <Box
        key={item.key}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          minHeight: 50,
          padding: "10px 0",
          borderBottom: "1px solid var(--border-color)",
          flexDirection: isRTL === "rtl" ? "row-reverse" : "row",
          ...item.addressGridStyle
        }}
      >
        <Typography
          style={{
            fontSize: 15,
            fontWeight: 500,
            color: "var(--text-secondary)",
            textAlign: isRTL === "rtl" ? "right" : "left",
            ...item.typographyStyleAddress
          }}
        >
          {item.label}
        </Typography>
        <Typography
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: "var(--text-main)",
            textAlign: isRTL === "rtl" ? "left" : "right",
            wordBreak: "break-all",
            maxWidth: "65%",
            ...item.style,
          }}
        >
          {item.value}
        </Typography>
      </Box>
    );
  };

  const renderTypography = (text) => {
    return (
      <Typography
      className={classes.txt}
      style={{
        borderBottomRightRadius: isRTL ? "90px" : "",
        borderBottomLeftRadius: isRTL ? "" : "90px",
      }}
      variant="h5"
      >
        {text}
      </Typography>
    );
  };

  return (
    <div className="responsive-container" style={{ minHeight: '100vh', backgroundColor: 'var(--bg-main)' }}>
      <div dir={isRTL === "rtl" ? "rtl" : "ltr"}
        style={{
          marginBottom: 20,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
          flexDirection: isRTL === "rtl" ? "row-reverse" : "row"
        }}
      >
        <Button
          variant="outlined"
          onClick={() => {
            navigate("/bookings");
          }}
          sx={{
            textTransform: 'none',
            backgroundColor: 'rgba(233, 174, 49, 0.06)',
            borderColor: 'rgba(233, 174, 49, 0.3)',
            color: '#E9AE31',
            borderRadius: '20px',
            fontWeight: 600,
            fontSize: '13px',
            padding: '5px 14px',
            display: 'inline-flex',
            alignItems: 'center',
            '&:hover': {
              backgroundColor: 'rgba(233, 174, 49, 0.15)',
              borderColor: '#E9AE31',
              boxShadow: '0 0 8px rgba(233, 174, 49, 0.2)'
            }
          }}
        >
          <ArrowBackIcon style={{ fontSize: '16px', marginRight: '6px' }} />
          {t("go_back") || "Go Back"}
        </Button>
        {/* Cancel Booking Button */}
        {(data?.status === "NEW" || data?.status === "ACCEPTED" || data?.status === "PAYMENT_PENDING") && (
          <Button
            variant="outlined"
            onClick={handleCancelBooking}
            style={{
              textTransform: 'none',
              borderColor: '#ef4444',
              color: '#ef4444',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '14px',
              padding: '6px 16px'
            }}
          >
            {t("cancel_booking")}
          </Button>
        )}
        {/* Driver Trip Control Buttons */}
        {role === 'driver' && data?.driver === auth.profile.uid && (
          <div style={{ display: 'flex', gap: '10px' }}>
            {data.status === 'ACCEPTED' && (
              <Button
                variant="contained"
                onClick={handleArrive}
                style={{
                  textTransform: 'none',
                  backgroundColor: '#10b981',
                  color: 'white',
                  borderRadius: '8px',
                  fontWeight: 600,
                  fontSize: '14px',
                  padding: '6px 16px'
                }}
              >
                {t("arrive") || "Arrived at Pickup"}
              </Button>
            )}
            {data.status === 'ARRIVED' && (
              <Button
                variant="contained"
                onClick={handleStartTrip}
                style={{
                  textTransform: 'none',
                  backgroundColor: '#E9AE31',
                  color: 'white',
                  borderRadius: '8px',
                  fontWeight: 600,
                  fontSize: '14px',
                  padding: '6px 16px'
                }}
              >
                {t("start_trip") || "Start Trip"}
              </Button>
            )}
            {data.status === 'STARTED' && (
              <Button
                variant="contained"
                onClick={handleEndTrip}
                style={{
                  textTransform: 'none',
                  backgroundColor: '#E9AE31',
                  color: 'white',
                  borderRadius: '8px',
                  fontWeight: 600,
                  fontSize: '14px',
                  padding: '6px 16px'
                }}
              >
                {t("end_trip") || "End Trip"}
              </Button>
            )}
            {(data.status === 'PENDING' || data.status === 'REACHED') && (
              <Button
                variant="contained"
                onClick={handleCompletePayment}
                style={{
                  textTransform: 'none',
                  backgroundColor: '#10b981',
                  color: 'white',
                  borderRadius: '8px',
                  fontWeight: 600,
                  fontSize: '14px',
                  padding: '6px 16px'
                }}
              >
                {data.payment_mode === 'cash' 
                  ? (t("complete_cash_payment") || "Collect Cash & Complete")
                  : (t("complete_payment") || "Complete Payment")
                }
              </Button>
            )}
          </div>
        )}
        {/* Customer Payment Button */}
        {role === 'customer' && data?.customer === auth.profile.uid && (data.status === 'PENDING' || data.status === 'REACHED') && (
          <div style={{ display: 'flex', gap: '10px' }}>
            <Button
              variant="contained"
              onClick={handleCustomerPay}
              style={{
                textTransform: 'none',
                backgroundColor: '#10b981',
                color: 'white',
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '14px',
                padding: '6px 16px'
              }}
            >
              {t("pay_now") || "Pay Now"}
            </Button>
          </div>
        )}
        {/* Customer Review Button */}
        {role === 'customer' && data?.customer === auth.profile.uid && data.status === 'COMPLETE' && data.rating === undefined && (
          <div style={{ display: 'flex', gap: '10px' }}>
            <Button
              variant="contained"
              onClick={() => setOpenRatingDialog(true)}
              style={{
                textTransform: 'none',
                backgroundColor: '#E9AE31',
                color: 'var(--bg-main)',
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '14px',
                padding: '6px 16px'
              }}
            >
              {t("rate_driver") || "Leave a Review"}
            </Button>
          </div>
        )}
      </div>

      {data && data.status === 'ARRIVED' && data.driver_arrive_time && (
        <Card 
          className={classes.card}
          style={{ 
            padding: '24px', 
            marginBottom: '24px', 
            border: loadingTimer.isExceeded ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid rgba(233, 174, 49, 0.3)',
            background: 'linear-gradient(135deg, rgba(233, 174, 49, 0.05) 0%, rgba(233, 174, 49, 0.01) 100%)',
            borderRadius: '16px',
            boxShadow: 'var(--shadow-sm)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', flexDirection: isRTL === 'rtl' ? 'row-reverse' : 'row' }}>
            <div style={{ 
              width: '36px', 
              height: '36px', 
              borderRadius: '50%', 
              backgroundColor: loadingTimer.isExceeded ? 'rgba(239, 68, 68, 0.1)' : 'rgba(233, 174, 49, 0.1)', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
              <AccessTimeIcon style={{ color: loadingTimer.isExceeded ? '#ef4444' : '#E9AE31' }} />
            </div>
            <div>
              <Typography variant="h6" style={{ fontWeight: 700, color: 'var(--text-main)', fontFamily: 'Outfit, sans-serif' }}>
                {t('waiting_timer', 'Waiting Timer')}
              </Typography>
              <Typography variant="caption" style={{ color: 'var(--text-secondary)' }}>
                {t('driver_arrived_status_subtitle', 'The driver has arrived at the pickup location.')}
              </Typography>
            </div>
          </div>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Box style={{ 
                background: 'var(--bg-main)', 
                padding: '16px', 
                borderRadius: '12px', 
                border: '1px solid var(--border-color)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexDirection: isRTL === 'rtl' ? 'row-reverse' : 'row'
              }}>
                <Typography variant="body2" style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>
                  {t('wait_time_elapsed', 'Elapsed Waiting Time')}:
                </Typography>
                <Typography variant="subtitle1" style={{ fontWeight: 700, color: 'var(--text-main)' }}>
                  {String(loadingTimer.elapsedMins).padStart(2, '0')}:{String(loadingTimer.elapsedSecs).padStart(2, '0')}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              {loadingTimer.isExceeded ? (
                <Box style={{ 
                  background: 'rgba(239, 68, 68, 0.05)', 
                  padding: '16px', 
                  borderRadius: '12px', 
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: isRTL === 'rtl' ? 'row-reverse' : 'row' }}>
                    <Typography variant="body2" style={{ color: '#ef4444', fontWeight: 600 }}>
                      {t('free_time_exceeded_by', 'Free Time Exceeded by')}:
                    </Typography>
                    <Typography variant="subtitle1" style={{ fontWeight: 700, color: '#ef4444' }}>
                      {loadingTimer.extraMins} {t('mins', 'Mins')}
                    </Typography>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: isRTL === 'rtl' ? 'row-reverse' : 'row' }}>
                    <Typography variant="body2" style={{ color: '#ef4444', fontWeight: 600 }}>
                      {t('extra_surcharge', 'Extra Surcharge')}:
                    </Typography>
                    <Typography variant="subtitle1" style={{ fontWeight: 800, color: '#ef4444' }}>
                      {settings?.symbol}{parseFloat(loadingTimer.extraCharge).toFixed(settings?.decimal || 2)}
                    </Typography>
                  </div>
                </Box>
              ) : (
                <Box style={{ 
                  background: 'rgba(16, 185, 129, 0.05)', 
                  padding: '16px', 
                  borderRadius: '12px', 
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexDirection: isRTL === 'rtl' ? 'row-reverse' : 'row'
                }}>
                  <Typography variant="body2" style={{ color: '#10b981', fontWeight: 600 }}>
                    {t('free_time_remaining', 'Free Time Remaining')}:
                  </Typography>
                  <Typography variant="subtitle1" style={{ fontWeight: 700, color: '#10b981' }}>
                    {String(loadingTimer.remainingMins).padStart(2, '0')}:{String(loadingTimer.remainingSecs).padStart(2, '0')}
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </Card>
      )}

      <Box sx={{ flexGrow: 1 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            {data && data.pickup && (
              <Card 
                className={classes.card}
                style={{ 
                  height: '350px', 
                  marginBottom: '24px', 
                  borderRadius: '16px',
                  overflow: 'hidden',
                  border: 'var(--card-border)',
                  boxShadow: 'var(--shadow-sm)'
                }}
              >
                <GoogleMap
                  zoom={13}
                  center={driverCoords ? { lat: parseFloat(driverCoords.lat), lng: parseFloat(driverCoords.lng) } : { lat: parseFloat(data.pickup.lat), lng: parseFloat(data.pickup.lng) }}
                  mapContainerStyle={{ height: '100%', width: '100%' }}
                >
                  {/* Pickup Marker */}
                  {data.pickup && (
                    <Marker
                      position={{ lat: parseFloat(data.pickup.lat), lng: parseFloat(data.pickup.lng) }}
                      label="P"
                    />
                  )}

                  {/* Dropoff Marker */}
                  {data.drop && (
                    <Marker
                      position={{ lat: parseFloat(data.drop.lat), lng: parseFloat(data.drop.lng) }}
                      label="D"
                    />
                  )}

                  {/* Driver Marker */}
                  {driverCoords && ['ACCEPTED', 'ARRIVED', 'STARTED'].includes(data.status) && (
                    <Marker
                      position={{ lat: parseFloat(driverCoords.lat), lng: parseFloat(driverCoords.lng) }}
                      icon={{
                        path: window.google?.maps?.SymbolPath?.FORWARD_CLOSED_ARROW || 0,
                        scale: 6,
                        fillColor: '#3b82f6',
                        fillOpacity: 0.9,
                        strokeWeight: 2,
                        strokeColor: '#ffffff'
                      }}
                    />
                  )}

                  {/* Route Directions Path */}
                  {directionsResponse && (
                    <DirectionsRenderer
                      directions={directionsResponse}
                      options={{
                        polylineOptions: {
                          strokeColor: ['ACCEPTED', 'ARRIVED'].includes(data.status) ? '#10b981' : '#ef4444',
                          strokeWeight: 5,
                          strokeOpacity: 0.85
                        },
                        suppressMarkers: true
                      }}
                    />
                  )}
                </GoogleMap>
              </Card>
            )}
            <Grid item>
              <Card className={classes.card}> 
              {renderTypography(t("ride_information"))}
                <Grid container direction="column" style={{ padding: 10 }}>
                  {data &&
                    [
                      { key: "booking_id", label: t("booking_id"), value: data.id },
                      { key: "booking_ref", label: t("booking_ref"), value: data.reference },
                      {
                        key: "booking_status_web",
                        label: t("booking_status_web"),
                        value: t(data.status),
                        style: {
                          backgroundColor:
                            data?.status === "CANCELLED"
                              ? "rgba(239, 68, 68, 0.2)"
                              : data?.status === "COMPLETE"
                              ? "rgba(16, 185, 129, 0.2)"
                              : "rgba(233, 174, 49, 0.2)",
                          color:
                            data?.status === "CANCELLED"
                              ? "#ef4444"
                              : data?.status === "COMPLETE"
                              ? "#10b981"
                              : "#e9ae31",
                          border:
                            data?.status === "CANCELLED"
                              ? "1px solid #ef4444"
                              : data?.status === "COMPLETE"
                              ? "1px solid #10b981"
                              : "1px solid #e9ae31",
                          fontWeight: "bold",
                          borderRadius: "20px",
                          padding: "4px 12px",
                          fontSize: "12px",
                          display: "inline-block",
                          textAlign: "center",
                        },
                      },
                      {
                        key: "trip_start_date", label: t("trip_start_date"), value: data?.tripdate
                          ? moment(data?.tripdate).format("lll")
                          : ""
                      },
                      { key: "trip_start_time", label: t("trip_start_time"), value: data?.trip_start_time },
                      { key: "trip_end_time", label: t("trip_end_time"), value: data?.trip_end_time },
                      {
                        key: "parcel_type_web", label: t("parcel_type_web"), value: data.parcelTypeSelected && data.parcelActive !== false
                          ? data.parcelTypeSelected.description +
                          " (" +
                          data.parcelTypeSelected.amount +
                          " x " + (data.parcelQuantity || 1) + ")"
                          : ""
                      },
                      {
                        key: "parcel_option", label: t("parcel_option"), value: data.optionSelected && data.optionActive !== false
                          ? data.optionSelected.description +
                          " (" +
                          data.optionSelected.amount +
                          " x " + (data.passengerCount || 1) + ")"
                          : ""
                      },
                      { key: "deliveryPerson", label: t("deliveryPerson"), value: data?.deliveryPerson },
                      {
                        key: "deliveryPersonPhone", label: t("deliveryPersonPhone"), value: data.deliveryPersonPhone ? (settings.AllowCriticalEditsAdmin
                          ? data.deliveryPersonPhone
                          : t("hidden_demo")) : ""
                      },
                      { key: "take_pickup_image_web", label: t("take_pickup_image_web"), value: data.pickup_image ? data.pickup_image : "", type: 'image', alt: "pickup_image" },
                      { key: "take_deliver_image_web", label: t("take_deliver_image_web"), value: data.deliver_image ? data.deliver_image : "", type: 'image', alt: "deliver_image" },
                      { key: "tripInstructions", label: t("tripInstructions"), value: data?.tripInstructions },
                      { key: "cancellation_reason", label: t("cancellation_reason"), value: data?.reason },
                      { key: "otp", label: t("otp"), value: data?.otp },
                      { key: "total_time", label: t("total_time"), value: data?.total_trip_time },
                      {
                        key: "distance_web", label: t("distance_web"), value: data.distance ? (settings.convert_to_mile
                          ? data?.distance + t("mile")
                          : data?.distance + " " + t("km")) : ""
                      },
                      { key: "pickUpInstructions_web", label: t("pickUpInstructions_web"), value: data?.pickUpInstructions },
                      { key: "deliveryInstructions", label: t("deliveryInstructions"), value: data?.deliveryInstructions },
                      { key: "otherPerson", label: t("otherPerson"), value: data?.otherPerson },
                      { key: "otherPersonPhone", label: t("otherPersonPhone"), value: data?.otherPersonPhone },
                      { key: "feedback", label: t("feedback"), value: data?.feedback },
                      {
                        key: "pickup_address", label: t("pickup_address"), value: data?.pickupAddress, typographyStyleAddress: { color: "#10b981" }
                      },
                      { key: "drop_address", label: t("drop_address"), value: data?.dropAddress, typographyStyleAddress: { color: "#ef4444" } },
                    ].map((item) =>
                      item.type === "image" && item.value ? (
                        <Box
                          key={item.key}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            minHeight: 80,
                            padding: "10px 0",
                            borderBottom: "1px solid var(--border-color)",
                            flexDirection: isRTL === "rtl" ? "row-reverse" : "row",
                          }}
                        >
                          <Typography
                            style={{
                              fontSize: 15,
                              fontWeight: 500,
                              color: "var(--text-secondary)",
                              textAlign: isRTL === "rtl" ? "right" : "left",
                            }}
                          >
                            {item.label}
                          </Typography>
                          <img
                            src={item.value}
                            alt={item.alt}
                            style={{
                              width: 100,
                              height: 75,
                              borderRadius: "8px",
                              objectFit: "cover",
                              border: "1px solid var(--border-color)"
                            }}
                          />
                        </Box>
                      ) :
                        item.value ?renderGridItem(item, isRTL) : null
                    )}
                </Grid>
              </Card>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <Grid item>
              <Card className={classes.card}>
                {renderTypography(t("payment_info"))}
                <Grid container direction="column" style={{ paddingRight: 15, paddingLeft: 15, paddingBottom: 15 }}>
                  {data &&
                    [
                      {
                        key: "trip_cost", label: t("trip_cost"), value: settings.swipe_symbol
                          ? data?.trip_cost + " " + settings.symbol
                          : settings.symbol + " " + data?.trip_cost
                      },
                      {
                        key: "Customer_paid", label: t("Customer_paid"), value: data?.customer_paid ? (settings.swipe_symbol
                            ? data?.customer_paid + " " + settings.symbol
                            : settings.symbol + " " + data?.customer_paid) : ""
                      },
                      {
                        key: "discount_ammount",
                        label: t("discount_ammount"),
                        value: settings.swipe_symbol
                          ? data?.discount + " " + settings.symbol
                          : settings.symbol + " " + data?.discount,
                      },
                      {
                        key: "driver_share", label: t("driver_share"), value: settings.swipe_symbol
                          ? data?.driver_share + " " + settings.symbol
                          : settings.symbol + " " + data?.driver_share
                      },
                      {
                        key: "fleet_admin_comission", label: t("fleet_admin_comission"), value: data?.fleetCommission && parseFloat(data?.fleetCommission) > 0 ? (settings.swipe_symbol
                          ? data?.fleetCommission + " " + settings.symbol
                          : settings.symbol + " " + data?.fleetCommission) : ""
                      },
                      {
                        key: "convenience_fee", label: t("convenience_fee"), value: settings.swipe_symbol
                          ? data?.convenience_fees + " " + settings.symbol
                          : settings.symbol + " " + data?.convenience_fees
                      },
                      {
                        key: "extraLoadingMins", label: t("extraLoadingMins"), value: data?.extraLoadingMins ? data.extraLoadingMins + " " + t("mins") : ""
                      },
                      {
                        key: "extraLoadingCharge", label: t("extraLoadingCharge"), value: data?.extraLoadingCharge ? (settings.swipe_symbol
                          ? data.extraLoadingCharge + " " + settings.symbol
                          : settings.symbol + " " + data.extraLoadingCharge) : ""
                      },
                      {
                        key: "cancellationFee", label: t("cancellationFee"), value: settings.swipe_symbol
                          ? data.cancellationFee
                            ? data.cancellationFee
                            : (0).toFixed(settings.decimal) +
                            " " +
                            settings.symbol
                          : settings.symbol + " " + data.cancellationFee
                            ? data.cancellationFee
                            : (0).toFixed(settings.decimal)
                      },
                      {
                        key: "payment_gateway", label: t("payment_gateway"), value: data?.gateway
                      },
                      { key: "payment_mode_web", label: t("payment_mode_web"), value: data?.payment_mode ? data.payment_mode : "" },
                      {
                        key: "cash_payment_amount", label: t("cash_payment_amount"), value: data?.cashPaymentAmount ? (settings.swipe_symbol
                          ? data?.cashPaymentAmount + " " + settings.symbol
                          : settings.symbol + " " + data?.cashPaymentAmount) : ""
                      },
                      {
                        key: "card_payment_amount", label: t("card_payment_amount"), value:
                          data?.cardPaymentAmount ? (settings.swipe_symbol
                            ? data?.cardPaymentAmount + " " + settings.symbol
                            : settings.symbol + " " + data?.cardPaymentAmount) : ""
                      },
                      {
                        key: "wallet_payment_amount", label: t("wallet_payment_amount"), value:
                          data?.usedWalletMoney ? (settings.swipe_symbol
                            ? data?.usedWalletMoney + " " + settings.symbol
                            : settings.symbol + " " + data?.usedWalletMoney) : ""
                      },
                      {
                        key: "payable_ammount", label: t("payable_ammount"), value: data?.payableAmount ? (settings.swipe_symbol
                          ? data?.payableAmount + " " + settings.symbol
                          : settings.symbol + " " + data?.payableAmount) : ""
                      },
                    ].map((item) =>
                      item.value ? renderGridItem(item, isRTL): null
                    )}
                </Grid>
              </Card>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <Grid item>
              <Card className={classes.card}>
                {renderTypography(t("driver_info"))}
                <Grid container direction="column" style={{ padding: 15 }}>
                  <Grid
                    item
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {data?.driver_image ? (
                      <Avatar
                        alt="driver profile image"
                        src={data?.driver_image}
                        style={{ width: 100, height: 100, objectFit: "cover" }}
                      />
                    ) : (
                      <AccountCircleIcon sx={{ width: 100, height: 100 }} />
                    )}
                  </Grid>
                  {data &&
                    [
                      { key: "driver_id", label: t("driver_id"), value: data?.driver ? data.driver : "" },
                      { key: "driver_name", label: t("driver_name"), value: data?.driver_name ? data.driver_name : "" },
                      {
                        key: "driver_contact", label: t("driver_contact"), value: data?.driver_contact ? (settings.AllowCriticalEditsAdmin
                          ? data?.driver_contact
                          : t("hidden_demo")) : ""
                      },
                      {
                        key: "driver_email", label: t("driver_email"), value: data?.driver_email ? (settings.AllowCriticalEditsAdmin
                          ? data?.driver_email
                          : t("hidden_demo")) : ""
                      },
                      { key: "car_type", label: t("car_type"), value: data?.carType ? data.carType : "" },
                      { key: "vehicle_no", label: t("vehicle_no"), value: data?.vehicle_number ? data.vehicle_number : "" },
                      { key: "fleetadmins", label: t("fleetadmins"), value: data?.fleetadmin && role === 'admin' ? data.fleetadmin : "" },
                      { key: "device_id", label: t("device_id"), value: data?.driverDeviceId ? data.driverDeviceId : "" },

                    ].map((item) =>
                      item.value ? renderGridItem(item, isRTL): null
                    )}
                </Grid>
              </Card>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <Grid item>
              <Card className={classes.card}>
                {renderTypography(t("customer_info"))}
                <Grid container direction="column" style={{ padding: 15 }}>
                  <Grid
                    item
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {data?.customer_image ? (
                      <Avatar
                        alt="customer profile image"
                        src={data?.customer_image}
                        style={{ width: 100, height: 100, objectFit: "cover" }}
                      />
                    ) : (
                      <AccountCircleIcon sx={{ width: 100, height: 100 }} />
                    )}
                  </Grid>
                  {data &&
                    [
                      { key: "customer_id", label: t("customer_id"), value: data?.customer ? data.customer : "" },
                      { key: "customer_name", label: t("customer_name"), value: data?.customer_name ? data.customer_name : "" },
                      {
                        key: "customer_contact", label: t("customer_contact"), value: data?.customer_contact ? (settings.AllowCriticalEditsAdmin
                          ? data?.customer_contact
                          : t("hidden_demo")) : ""
                      },
                      {
                        key: "customer_email", label: t("customer_email"), value: data?.customer_email ? (settings.AllowCriticalEditsAdmin
                          ? data?.customer_email
                          : t("hidden_demo")) : ""
                      },

                    ].map((item) =>
                      item.value ? renderGridItem(item, isRTL): null
                    )}
                </Grid>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Box>
      <ConfirmationDialogRaw
        open={openConfirm}
        onClose={onConfirmClose}
        value={""}
      />
      {/* OTP Input Dialog */}
      <Dialog
        open={openOtpDialog}
        onClose={() => setOpenOtpDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle style={{ textAlign: 'center', fontWeight: 700 }}>
          {t("enter_otp") || "Enter Start OTP"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" style={{ color: 'var(--text-secondary)', marginBottom: '16px', textAlign: 'center' }}>
            {t("otp_prompt") || "Please ask the customer for the OTP to start the journey."}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label={t("otp") || "OTP Code"}
            type="text"
            fullWidth
            variant="outlined"
            value={otpInput}
            onChange={(e) => setOtpInput(e.target.value)}
            error={!!otpError}
            helperText={otpError}
            inputProps={{ style: { textAlign: 'center', fontSize: '20px', letterSpacing: '4px', fontWeight: 700 } }}
          />
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <Button onClick={() => setOpenOtpDialog(false)} style={{ textTransform: 'none', color: 'var(--text-secondary)' }}>
            {t("cancel")}
          </Button>
          <Button onClick={handleOtpVerify} variant="contained" style={{ textTransform: 'none', backgroundColor: 'var(--primary)' }}>
            {t("verify") || "Verify & Start"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rating / Review Dialog */}
      <Dialog
        open={openRatingDialog}
        onClose={(event, reason) => {
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            setOpenRatingDialog(false);
          }
        }}
        disableEscapeKeyDown
        maxWidth="xs"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: 'var(--bg-card)',
            color: 'var(--text-main)',
            border: 'var(--card-border)',
            borderRadius: '16px',
            padding: '16px'
          }
        }}
      >
        <DialogTitle style={{ fontWeight: 700, textAlign: 'center', pb: 1 }}>
          {t("rate_ride") || "Rate Your Driver"}
        </DialogTitle>
        <DialogContent style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', padding: '8px 24px' }}>
          
          {/* Driver Profile Header */}
          {data && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', marginBottom: '8px', width: '100%' }}>
              <Avatar 
                src={data.driver_image} 
                style={{ width: '80px', height: '80px', border: '3px solid #E9AE31', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
              >
                <AccountCircleIcon style={{ width: '80px', height: '80px' }} />
              </Avatar>
              <Typography variant="h6" style={{ fontWeight: 700, color: 'var(--text-main)', marginTop: '4px' }}>
                {data.driver_name}
              </Typography>
              
              {/* Driver's Current Rating (Static Stars) */}
              {data.driverRating ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <StarIcon style={{ color: '#E9AE31', fontSize: '18px' }} />
                  <Typography variant="body2" style={{ fontWeight: 600, color: 'var(--text-main)' }}>
                    {data.driverRating}
                  </Typography>
                </div>
              ) : null}

              {/* Vehicle Pill Badges */}
              {(data.vehicleModel || data.vehicleMake || data.vehicle_number) && (
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'center', marginTop: '2px' }}>
                  {data.vehicleMake && (
                    <span style={{ backgroundColor: 'rgba(255,255,255,0.06)', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                      {data.vehicleMake}
                    </span>
                  )}
                  {data.vehicleModel && (
                    <span style={{ backgroundColor: 'rgba(255,255,255,0.06)', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                      {data.vehicleModel}
                    </span>
                  )}
                  {data.vehicle_number && (
                    <span style={{ backgroundColor: 'rgba(233, 174, 49, 0.12)', border: '1px solid rgba(233, 174, 49, 0.25)', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', color: '#E9AE31', fontWeight: 600 }}>
                      {data.vehicle_number}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          <div style={{ borderBottom: '1px solid var(--border-color)', width: '100%', margin: '4px 0' }} />

          <Typography variant="subtitle1" style={{ fontWeight: 700, color: 'var(--text-main)', textAlign: 'center' }}>
            {t("how_your_trip") || "How is your trip?"}
          </Typography>
          
          <Typography variant="body2" style={{ color: 'var(--text-secondary)', textAlign: 'center', marginTop: '-10px' }}>
            {t("your_feedback_test") || "Your feedback will help us improve driving experience better."}
          </Typography>
          
          {/* Star Selection */}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {[1, 2, 3, 4, 5].map((star) => (
              <IconButton 
                key={star} 
                onClick={() => setRatingValue(star)}
                style={{ padding: '8px' }}
              >
                {star <= ratingValue ? (
                  <StarIcon style={{ color: '#E9AE31', fontSize: '40px' }} />
                ) : (
                  <StarBorderIcon style={{ color: 'var(--text-secondary)', fontSize: '40px' }} />
                )}
              </IconButton>
            ))}
          </div>

          <TextField
            multiline
            rows={3}
            fullWidth
            placeholder={t("your_feedback") || "Your feedback (optional) ..."}
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            InputProps={{
              style: {
                color: 'var(--text-main)',
                backgroundColor: 'var(--bg-main)',
                border: '1px solid var(--border-color)',
                borderRadius: '8px'
              }
            }}
          />
        </DialogContent>
        <DialogActions style={{ justifyContent: 'space-between', padding: '8px 24px 24px 24px' }}>
          <Button 
            onClick={handleSkipRating}
            style={{ color: 'var(--text-secondary)', textTransform: 'none', fontWeight: 600 }}
          >
            {t("skip") || "Skip"}
          </Button>
          <Button 
            variant="contained"
            onClick={handleSubmitRating}
            disabled={ratingValue === 0}
            style={{ 
              backgroundColor: ratingValue === 0 ? 'rgba(233, 174, 49, 0.4)' : '#E9AE31', 
              color: 'var(--bg-main)', 
              textTransform: 'none', 
              fontWeight: 700,
              borderRadius: '8px'
            }}
          >
            {t("submit") || "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default BookingDetails;
